import { Button } from '@brighterion/br-ui';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { RoleService } from 'Services';
import { shouldShowCompareSimulations, shouldShowSimulations, getUser } from 'Selectors';
import { useTemplateStore } from 'Hooks/zustand/useTemplateStore';
import useCreateSimulation from 'Hooks/query-hooks/simulations/useCreateSimulation';
import useIsSimulating from 'Hooks/useIsSimulating';
import { SIMULATION_STATUS } from 'Common/enums';
import useReduxModal from 'Hooks/reduxHelperHooks/useReduxModal';
import NewSimulationModal from '../NewSimulationModal';
import NewSimulationSuccessModal from '../NewSimulationSuccessModal';

const NewSimulationButton = () => {
    const [formValues, setFormValues] = useState({} as NewSimulationFormType);
    const [modalIsOpen, setModalIsOpen] = useState(false);
    const [isSimulationSuccess, setIsSimulationSuccess] = useState(false);
    const { userId } = useSelector(getUser);
    const { templateRepositoryId } = useTemplateStore.getState().template;
    const canAccessSimulations = RoleService.hasDesignAccess;
    const { lastMinuteSimulations } = useIsSimulating(
        shouldShowSimulations() && canAccessSimulations,
        () => {},
        () => {},
        30 * 60 * 1000 // 30 minutes
    );

    /* 
    1. disable new simulation btn as initial state to prevent edge cases when computing
    2. if we get empty simulation data for user, enable new simulation btn
    3. disable the btn ONLY if a simulation within our window is in DEPLOYING state
    */
    let startSimulationBtnDisabled = true;
    if (!lastMinuteSimulations || !lastMinuteSimulations.data || !lastMinuteSimulations.data[userId]) {
        startSimulationBtnDisabled = false;
    } else {
        const preventNewSimulationStates = [SIMULATION_STATUS.DEPLOYING];
        const userSimulationStates = Object.keys(lastMinuteSimulations.data[userId]);
        const foundDeploying = userSimulationStates.some(
            (simulationState) =>
                preventNewSimulationStates.includes(simulationState as any) &&
                lastMinuteSimulations.data[userId][simulationState].total > 0
        );
        if (!foundDeploying) {
            startSimulationBtnDisabled = false;
        }
    }

    const { addModal } = useReduxModal();
    const { t } = useTranslation();

    const showNewSimulationSuccessModal = () => {
        const newSimulationSuccessModal = <NewSimulationSuccessModal formValues={formValues} />;
        addModal({
            id: 'new-simulation-success',
            status: 'success',
            title: t('New Simulation Success'),
            cancelText: t('Close'),
            content: newSimulationSuccessModal
        });
    };

    useEffect(() => {
        if (isSimulationSuccess) {
            showNewSimulationSuccessModal();
        }
    }, [isSimulationSuccess]);

    const mutation = useCreateSimulation({
        mutationOptions: {
            onSuccess: () => {
                setModalIsOpen(false);
                setIsSimulationSuccess(true);
            },
            onError: (err?: any) => {
                setModalIsOpen(false);
                addModal({
                    id: 'new-simulation-error',
                    status: 'error',
                    title: t('Failed to create a new simulation'),
                    content: (
                        <>
                            <>{t('newSimulationError', { errMsg: err.data.message })}</>
                        </>
                    ),
                    cancelText: t('Close')
                });
            }
        },
        templateRepositoryId: templateRepositoryId!
    });

    const handleSubmit = async (values: NewSimulationFormType) => {
        setFormValues(values);
        const { start, duration, compare, ...rest } = values;
        const startInt = start.getTime();
        const processedFormValues = {
            start: startInt,
            end: startInt + duration,
            templateRepositoryId: templateRepositoryId!,
            ...rest,
            simulationType: (compare ? 'RULE_PERFORMANCE_COMPARE' : 'RULE_PERFORMANCE') as SimulationType
        };
        return mutation.mutate(processedFormValues);
    };

    return (
        <>
            <Button
                disabled={mutation.isLoading || startSimulationBtnDisabled}
                variant='secondary'
                size='small'
                onClick={() => setModalIsOpen(true)}
                onKeyDown={() => setModalIsOpen(true)}
            >
                {t('New Simulation')}
            </Button>
            <NewSimulationModal
                isOpen={modalIsOpen}
                isLoading={mutation.isLoading}
                closeModal={() => setModalIsOpen(false)}
                handleSubmit={handleSubmit}
                hideCompare={!shouldShowCompareSimulations()}
            />
        </>
    );
};

export default React.memo(NewSimulationButton);
